import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import api from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { todayIso, fmtDay } from "@/lib/cantieri";

export const TIPI = { anticipo: "Anticipo / pagamento", rimborso: "Rimborso spese" };

const TipoSwitch = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-border bg-secondary/50 p-0.5 col-span-2" data-testid="movimento-tipo">
    {Object.entries(TIPI).map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`movimento-tipo-${k}`}
        className={`px-3 min-h-[40px] text-sm rounded-md font-medium transition-colors ${value === k ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{l}</button>
    ))}
  </div>
);

// Anticipi (scalano da quanto gli devi) e rimborsi (si aggiungono) di un operaio.
export const MovimentiDialog = ({ operaio, onClose, onChanged }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ tipo: "anticipo", data: todayIso(), importo: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const load = () => api.get(`/operai/${operaio.id}/movimenti`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [operaio.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/operai/${operaio.id}/movimenti`, { ...f, importo: Number(f.importo) });
      toast.success(f.tipo === "anticipo" ? "Anticipo registrato" : "Rimborso registrato");
      setF({ ...f, importo: "", note: "" }); load(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (m) => {
    if (ask !== m.id) { setAsk(m.id); return; }
    setAsk(null);
    const r = await api.delete(`/operai/movimenti/${m.id}`);
    toast.success(r.data.spesa_ripristinata ? "Eliminato: la spesa è tornata in Spese di gestione" : "Movimento eliminato");
    load(); onChanged();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="movimenti-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Anticipi e rimborsi · {operaio.nome}</DialogTitle>
          <DialogDescription>L'anticipo scala da quanto gli devi; il rimborso (es. benzina pagata da lui) si aggiunge.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-border p-3">
          <TipoSwitch value={f.tipo} onChange={(tipo) => setF({ ...f, tipo })} />
          <DateInput value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="movimento-date-input" />
          <Input type="number" min="0" step="0.01" inputMode="decimal" placeholder="Importo €" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} data-testid="movimento-amount-input" />
          <Input placeholder="Nota (facoltativa)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="col-span-2" data-testid="movimento-note-input" />
          <Button onClick={add} disabled={busy || !(Number(f.importo) > 0) || !f.data} className="col-span-2" data-testid="movimento-add-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Registra</>}
          </Button>
        </div>
        <div className="rounded-xl border border-border divide-y divide-border" data-testid="movimenti-list">
          {list.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`movimento-row-${m.id}`}>
              <span className="w-20 text-muted-foreground">{fmtDay(m.data)}</span>
              <span className="flex-1 min-w-0"><span className="font-medium">{TIPI[m.tipo]}</span>{m.note && <span className="block text-xs text-muted-foreground truncate">{m.note}</span>}</span>
              <span className={`font-semibold tabular-nums ${m.tipo === "anticipo" ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>{m.tipo === "anticipo" ? "−" : "+"}{euro(m.importo)}</span>
              {ask === m.id
                ? <Button variant="destructive" size="sm" onClick={() => del(m)} onBlur={() => setAsk(null)} data-testid={`movimento-delete-confirm-${m.id}`}>Elimina?</Button>
                : <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => del(m)} aria-label="Elimina" data-testid={`movimento-delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button>}
            </div>
          ))}
          {!list.length && <div className="p-6 text-center text-sm text-muted-foreground">Nessun anticipo o rimborso registrato.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MovimentiDialog;
